import { useEffect, useRef } from 'react'

/**
 * Surfaces that own their own keymap and would otherwise swallow — or be
 * hijacked by — a bare Space. `closest()` runs against the event's own target,
 * not the document, so this is not the `document.querySelector` the UI guide
 * bans.
 */
const EDITABLE_HOSTS = '.cm-editor, .milkdown, [contenteditable="true"]'

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return target.closest(EDITABLE_HOSTS) !== null
}

/**
 * Opens the command palette on Shift+Space, or on ⌘/Ctrl+K.
 *
 * Shift+Space is the fast one-handed binding, but Space is a printable
 * character, so it has to stand down anywhere text is being typed — otherwise
 * it eats a space in a duty script. ⌘K is the fallback everyone's hands
 * already know, and is immune to that class of problem; it needs its own
 * `preventDefault` because the browser claims it, which is why it lives here
 * rather than in `useHotKey` (whose `preventDefault` is commented out).
 *
 * `toggle` is read through a ref so a new closure each render doesn't churn
 * the listener.
 */
export function useCommandPaletteHotkey(toggle: () => void): void {
  const toggleRef = useRef(toggle)
  // eslint-disable-next-line react-hooks/refs
  toggleRef.current = toggle

  useEffect(() => {
    function handler(event: KeyboardEvent) {
      const isMetaK =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'k'

      const isShiftSpace =
        event.code === 'Space' &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isEditableTarget(event.target)

      if (!isMetaK && !isShiftSpace) return

      // Shift+Space scrolls the page up; ⌘K is claimed by the browser.
      event.preventDefault()
      toggleRef.current()
    }

    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
    }
  }, [])
}
